import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  EyeIcon,
  ScaleIcon,
  DocumentCheckIcon,
  BuildingLibraryIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

/* ============================================================
   Clarid demo landing page — config-driven, standalone.
   A personalized page sent to a specific prospect ahead of a
   demo. noindex. Company facts must be verified before use.
   ============================================================ */

const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}
function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, scale: 0.95 }} animate={isInView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.6, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}
function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ duration: 0.7, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

export interface DemoAgendaItem { title: string; body: string; }
export interface DemoCapability { title: string; description: string; }
export interface DemoConfig {
  docTitle: string;
  /** Company shown in the header lockup + throughout. */
  company: string;
  /** Prospect's first name for the personalized welcome. */
  firstName: string;
  eyebrow: string;
  headline: React.ReactNode;
  lead: string;
  fitHeadline: string;
  /** 2–3 short paragraphs on why Clarid fits their world (the tailored angle). */
  fitParagraphs: string[];
  agendaHeadline: string;
  agenda: DemoAgendaItem[];
  capabilities?: DemoCapability[];
  contact: { name: string; title: string; email: string };
}

// Clarid's core capabilities — the default demo tour. Pages can override.
const DEFAULT_CAPABILITIES: DemoCapability[] = [
  { title: 'Marketing Compliance', description: 'Every ad, email, social post, and disclosure checked against federal rules (Reg Z, UDAAP, TILA, FTC Act) with AI detection, a deterministic rules engine, and mandatory human approval — examiner-ready on every check.' },
  { title: 'HMDA', description: 'LAR validation with 160+ edit checks, ML-assisted LOS imports, geocoding, and submission-ready exports.' },
  { title: 'Fair Lending', description: 'Denial and pricing disparity testing, redlining with census-tract mapping, and formal risk assessments — tuned to FDIC, OCC, Fed, and NCUA standards.' },
  { title: 'CRA', description: 'Assessment-area construction, lending-distribution analysis, peer benchmarking, and examiner-ready performance-evaluation tables.' },
  { title: 'Exam & Board Packaging', description: 'Findings from every module assemble into one examiner-ready package and a board-ready summary — timestamped and hash-verified.' },
];

const CAP_ICONS = [EyeIcon, DocumentCheckIcon, ScaleIcon, BuildingLibraryIcon, ClipboardDocumentCheckIcon, ChartBarIcon];

export default function DemoLandingTemplate({ config }: { config: DemoConfig }) {
  const caps = config.capabilities ?? DEFAULT_CAPABILITIES;

  useEffect(() => {
    document.title = config.docTitle;
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', 'noindex, nofollow');
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
      document.title = 'Clarid — Clarity in every communication';
    };
  }, [config.docTitle]);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* Header · lockup */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <div className="flex items-center gap-5">
            <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
            <span className="text-slate-300 text-xl font-light">×</span>
            <span className="font-display text-xl md:text-2xl font-bold text-navy-900 tracking-tight">{config.company}</span>
          </div>
        </div>
      </header>

      {/* 1 · Hero */}
      <section className="pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp><p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-600 mb-8">{config.eyebrow}</p></FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-5xl mx-auto">{config.headline}</h1>
          </FadeUp>
          <FadeUp delay={0.2}><p className="text-xl md:text-2xl text-slate-500 max-w-4xl mx-auto leading-relaxed">{config.lead}</p></FadeUp>
        </div>
      </section>

      {/* 2 · Why this fits your world */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp><h2 className="text-4xl md:text-5xl font-display font-bold text-navy-900 text-center mb-14">{config.fitHeadline}</h2></FadeUp>
            <FadeUp delay={0.1}>
              <div className="space-y-7">
                {config.fitParagraphs.map((p, i) => (
                  <p key={i} className={`text-xl md:text-[1.35rem] leading-relaxed ${i === config.fitParagraphs.length - 1 ? 'font-semibold text-navy-900 mt-6' : 'text-slate-600'}`}>{p}</p>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* 3 · What we'll cover in the demo */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp><h2 className="text-4xl md:text-5xl font-display font-bold text-navy-900 text-center mb-16">{config.agendaHeadline}</h2></FadeUp>
            <div className="space-y-6 lg:space-y-8">
              {config.agenda.map((item, i) => (
                <FadeUp key={item.title} delay={i * 0.1}>
                  <div className="flex items-start gap-6 p-8 lg:p-10 bg-slate-50 rounded-2xl border border-slate-100">
                    <span className="flex-shrink-0 w-11 h-11 rounded-full bg-navy-900 text-white flex items-center justify-center text-base font-bold">{i + 1}</span>
                    <div>
                      <h3 className="text-xl md:text-2xl font-display font-bold text-navy-900 mb-2">{item.title}</h3>
                      <p className="text-lg text-slate-500 leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4 · The platform */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp><h2 className="text-4xl md:text-5xl font-display font-bold text-navy-900 text-center mb-16">The platform, in brief</h2></FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {caps.map((cap, i) => {
              const Icon = CAP_ICONS[i % CAP_ICONS.length];
              return (
                <ScaleIn key={cap.title} delay={i * 0.08}>
                  <div className="group p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:border-teal-200 hover:shadow-xl transition-all duration-500 h-full">
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 transition-all duration-300">
                        <Icon className="h-6 w-6 text-teal-600" />
                      </div>
                      <h3 className="text-2xl font-display font-bold text-navy-900">{cap.title}</h3>
                    </div>
                    <p className="text-lg text-slate-500 leading-relaxed">{cap.description}</p>
                  </div>
                </ScaleIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5 · Credibility */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp><h2 className="text-4xl md:text-5xl font-display font-bold text-navy-900 text-center mb-14">Built by a banker, validated by examiners</h2></FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 max-w-6xl mx-auto">
            <ScaleIn delay={0.1}>
              <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">Built by the buyer</h3>
                <p className="text-lg text-slate-500 leading-relaxed">Clarid's founder is a community-bank CMO who manages marketing compliance, HMDA reporting, and examiner relationships as part of his daily work — not engineers guessing at compliance needs.</p>
              </div>
            </ScaleIn>
            <ScaleIn delay={0.2}>
              <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">Validated by former examiners</h3>
                <p className="text-lg text-slate-500 leading-relaxed">A former FDIC Supervisory Examiner and a former Federal Reserve fair-lending subject-matter expert validate Clarid's rules and outputs against what examiners actually evaluate.</p>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* 6 · Contact */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-600 mb-6">Looking forward to the conversation, {config.firstName}</p>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">{config.contact.name}</p>
              <p className="text-lg text-slate-500">{config.contact.title}</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-teal-600">
                <EnvelopeIcon className="h-5 w-5" />
                <a href={`mailto:${config.contact.email}`} className="hover:text-teal-700 transition-colors">{config.contact.email}</a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-slate-400">
            <img src="/clarid-logo.png" alt="Clarid" className="h-7 opacity-40" />
            <span className="text-slate-200">×</span>
            <span className="font-display font-semibold text-sm">{config.company}</span>
          </div>
          <p className="text-slate-400 text-sm">&copy; {new Date().getFullYear()} Clarid. Confidential — prepared for {config.company}.</p>
          <div className="flex items-center gap-4 text-sm">
            <a href="/security" className="text-slate-400 hover:text-teal-600 transition-colors">Security</a>
            <a href="/privacy" className="text-slate-400 hover:text-teal-600 transition-colors">Privacy</a>
          </div>
        </div>
      </footer>

      <div className="sr-only">Prepared by Clarid for {config.company}. This is a demo overview from Clarid and does not imply an existing relationship or endorsement.</div>
    </div>
  );
}
